import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { AiOutlineTeam } from 'react-icons/ai';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';

import { Location, Unit } from '@core/booking/booking.types';
import config from '@config';

import { CityGroup } from './meeting-room-landing.types';

const GoogleMap = dynamic(() => import('@/atoms/map'), { ssr: false });
const MapMarker = dynamic(() => import('@/atoms/map-marker'), { ssr: false });

interface GeocodedLocation {
  locationId: string;
  location: Location;
  name: string;
  lat: number;
  lng: number;
  rooms: Unit[];
}

interface LocationsMapProps {
  cityGroups: CityGroup[];
  height?: string;
  onRoomSelect?: (room: Unit, location: Location) => void;
}

function cloudinaryUrl(url: string, width: number): string {
  if (!url || !url.includes('res.cloudinary.com')) return url;
  const transforms = `q_auto:best,f_auto,w_${width},c_fill,dpr_2.0`;
  return url.replace('/upload/', `/upload/${transforms}/`);
}

async function geocodeAddress(address: string): Promise<{ lat: number; lng: number } | null> {
  try {
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${
        config.backend.google.map.apiKey
      }`,
    );
    const data = await response.json();
    if (data.results?.[0]?.geometry?.location) {
      return data.results[0].geometry.location;
    }
  } catch {
    // Geocoding failed silently
  }
  return null;
}

function getUniqueLocations(cityGroups: CityGroup[]): { location: Location; rooms: Unit[] }[] {
  const map = new Map<string, { location: Location; rooms: Unit[] }>();
  for (const group of cityGroups) {
    for (const loc of group.locations) {
      const existing = map.get(loc.location.id);
      if (existing) {
        existing.rooms.push(...loc.rooms);
      } else {
        map.set(loc.location.id, { location: loc.location, rooms: [...loc.rooms] });
      }
    }
  }
  return Array.from(map.values());
}

const MarkerContent: FC<{
  loc: GeocodedLocation;
  onRoomSelect?: (room: Unit, location: Location) => void;
}> = ({ loc, onRoomSelect }) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const room = loc.rooms[currentIndex];
  const hasMultiple = loc.rooms.length > 1;
  const imageUrl = room?.image_link || '/placeholder.webp';

  const handlePrev = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setCurrentIndex((prev) => (prev - 1 + loc.rooms.length) % loc.rooms.length);
    },
    [loc.rooms.length],
  );

  const handleNext = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setCurrentIndex((prev) => (prev + 1) % loc.rooms.length);
    },
    [loc.rooms.length],
  );

  const handleClick = useCallback(() => {
    if (room && onRoomSelect) {
      onRoomSelect(room, loc.location);
    }
  }, [room, loc.location, onRoomSelect]);

  if (!room) return null;

  return (
    // Negative margins to counteract MapMarker's px-7 py-2 padding
    <div onClick={handleClick} className="cursor-pointer font-normal" style={{ width: 200, margin: '-8px -28px' }}>
      {/* Image */}
      <div className="relative overflow-hidden" style={{ height: 120, borderRadius: '6px 6px 0 0' }}>
        <Image
          src={cloudinaryUrl(imageUrl, 400)}
          alt={room.name}
          width={400}
          height={240}
          style={{ objectFit: 'cover', width: '100%', height: '100%' }}
          unoptimized
        />
        {/* Carousel arrows */}
        {hasMultiple && (
          <>
            <button
              onClick={handlePrev}
              className="absolute left-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white shadow-sm flex items-center justify-center border-none cursor-pointer p-0"
              style={{ opacity: 0.9 }}
            >
              <IoChevronBack size={14} style={{ color: '#404D61' }} />
            </button>
            <button
              onClick={handleNext}
              className="absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white shadow-sm flex items-center justify-center border-none cursor-pointer p-0"
              style={{ opacity: 0.9 }}
            >
              <IoChevronForward size={14} style={{ color: '#404D61' }} />
            </button>
            <div
              className="absolute bottom-1 right-1 px-2 py-0.5 rounded-full text-white"
              style={{ fontSize: 10, backgroundColor: 'rgba(0,0,0,0.6)' }}
            >
              {currentIndex + 1}/{loc.rooms.length}
            </div>
          </>
        )}
      </div>
      {/* Info */}
      <div className="px-2 py-2" style={{ borderRadius: '0 0 6px 6px' }}>
        <div className="font-bold text-xs truncate" style={{ color: '#404D61' }}>
          {room.name}
        </div>
        <div className="text-xs font-normal truncate" style={{ color: '#707070' }}>
          {loc.name}
        </div>
        <div className="flex items-center gap-1 mt-1" style={{ color: 'var(--main)' }}>
          <AiOutlineTeam size={12} />
          <span style={{ fontSize: 10, fontWeight: 500 }}>
            {t('landing.upTo', { count: Number(room.max_people_number) })}
          </span>
        </div>
      </div>
    </div>
  );
};

const LocationsMap: FC<LocationsMapProps> = ({ cityGroups, height = '100%', onRoomSelect }) => {
  const [geocoded, setGeocoded] = useState<GeocodedLocation[]>([]);
  const uniqueLocations = useMemo(() => getUniqueLocations(cityGroups), [cityGroups]);

  useEffect(() => {
    let cancelled = false;

    const resolve = async () => {
      const results: GeocodedLocation[] = [];

      for (const { location, rooms } of uniqueLocations) {
        // Use lat/lng from the API if available
        if (location.latitude && location.longitude) {
          results.push({
            locationId: location.id,
            location,
            name: location.name,
            lat: parseFloat(location.latitude),
            lng: parseFloat(location.longitude),
            rooms,
          });
          continue;
        }

        // Fallback: geocode the address
        const coords = await geocodeAddress(location.address);
        if (coords) {
          results.push({
            locationId: location.id,
            location,
            name: location.name,
            lat: coords.lat,
            lng: coords.lng,
            rooms,
          });
        }
      }

      if (!cancelled) {
        setGeocoded(results);
      }
    };

    resolve();
    return () => {
      cancelled = true;
    };
  }, [uniqueLocations]);

  const center = useMemo(() => {
    if (geocoded.length > 0) {
      return { lat: geocoded[0].lat, lng: geocoded[0].lng };
    }
    // Default center (US)
    return { lat: 39.8283, lng: -98.5795 };
  }, [geocoded]);

  const zoom = useMemo(() => {
    if (geocoded.length === 0) return 4;
    if (geocoded.length === 1) return 14;
    // Fit multiple markers: calculate bounds manually
    const lats = geocoded.map((g) => g.lat);
    const lngs = geocoded.map((g) => g.lng);
    const latSpan = Math.max(...lats) - Math.min(...lats);
    const lngSpan = Math.max(...lngs) - Math.min(...lngs);
    const maxSpan = Math.max(latSpan, lngSpan);
    if (maxSpan > 10) return 4;
    if (maxSpan > 5) return 6;
    if (maxSpan > 2) return 8;
    if (maxSpan > 0.5) return 10;
    if (maxSpan > 0.1) return 12;
    return 14;
  }, [geocoded]);

  return (
    <div style={{ height, width: '100%' }} className="rounded-xl overflow-hidden">
      <GoogleMap center={center} zoom={zoom} height={height}>
        {geocoded.map((loc) => (
          <MapMarker
            key={loc.locationId}
            lat={loc.lat}
            lng={loc.lng}
            isOpened
            content={<MarkerContent loc={loc} onRoomSelect={onRoomSelect} />}
          />
        ))}
      </GoogleMap>
    </div>
  );
};

export default LocationsMap;
